import axios from 'axios';
import tokenStore from '../Utils/tokenStore';

const IDENTITY_BASE_URL =
  process.env.REACT_APP_IDENTITY_API_BASE_URL || 'https://identity.vizybility.com';
const IDENTITY_API_VERSION =
  process.env.REACT_APP_IDENTITY_API_VERSION || 'v1';
const APPLICATION_ID = process.env.REACT_APP_APPLICATION_ID || '';

const identityApi = axios.create({
  baseURL: `${IDENTITY_BASE_URL}/api/${IDENTITY_API_VERSION}`,
  timeout: 30000,
  headers: {
    'Accept-Language': 'en',
    'User-Agent': typeof window !== 'undefined' ? window.navigator.userAgent : 'VizybilityRental',
  },
});

// ─── Error normalizer ────────────────────────────────────────────────────────

const createApiError = (error) => {
  if (error?.isApiError) return error;
  const response = error?.response;
  const payload = response?.data;
  const message =
    payload?.message || payload?.title || payload?.error || payload?.errors?.[0] || error?.message || 'Request failed.';
  const apiError = new Error(message);
  apiError.isApiError = true;
  apiError.status = response?.status;
  apiError.response = response;
  apiError.errors = payload?.errors || [];
  return apiError;
};

// ─── Token refresh interceptor ───────────────────────────────────────────────

let _isRefreshing = false;
let _refreshQueue = [];

const processRefreshQueue = (error, token = null) => {
  _refreshQueue.forEach((item) => (error ? item.reject(error) : item.resolve(token)));
  _refreshQueue = [];
};

identityApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config;

    const skipPaths = [
      '/auth/login',
      '/auth/token/refresh',
      '/auth/passkey/discover',
      '/auth/passkey/challenge',
      '/auth/passkey/authenticate',
    ];
    const isSkipped = skipPaths.some((p) => originalRequest?.url?.includes(p));

    if (error?.response?.status === 401 && !originalRequest?._retry && !isSkipped) {
      if (_isRefreshing) {
        return new Promise((resolve, reject) => {
          _refreshQueue.push({ resolve, reject });
        })
          .then((newToken) => {
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            return identityApi(originalRequest);
          })
          .catch((err) => Promise.reject(createApiError(err)));
      }

      originalRequest._retry = true;

      const slot = tokenStore.getStorageSlot();
      // rm === undefined → old session → treat as allowed to refresh
      const rememberMe = slot?.rm !== false;
      const refreshToken = slot?.r;

      if (!rememberMe || !refreshToken) {
        tokenStore.removeToken();
        if (typeof window !== 'undefined' && window.location.pathname !== '/') {
          window.location.href = '/';
        }
        return Promise.reject(createApiError(new Error('Session expired. Please sign in again.')));
      }

      _isRefreshing = true;

      try {
        const currentToken = slot?.t;
        const refreshResponse = await identityApi.post('/auth/token/refresh', {
          token: currentToken,
          refreshToken,
        });

        const data = refreshResponse?.data?.data ?? refreshResponse?.data;
        const newToken = data?.token || data?.Token;
        const newRefreshToken = data?.refreshToken || data?.RefreshToken;

        if (!newToken) throw new Error('Token refresh did not return a new token.');

        tokenStore.saveToken({
          token: newToken,
          refreshToken: newRefreshToken || refreshToken,
          profile: slot?.p || null,
          metadata: slot?.m || null,
          rememberMe: slot?.rm !== false,
        });

        processRefreshQueue(null, newToken);
        originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
        return identityApi(originalRequest);
      } catch (refreshError) {
        processRefreshQueue(refreshError, null);
        tokenStore.removeToken();
        if (typeof window !== 'undefined' && window.location.pathname !== '/') {
          window.location.href = '/';
        }
        return Promise.reject(createApiError(refreshError));
      } finally {
        _isRefreshing = false;
      }
    }

    return Promise.reject(createApiError(error));
  },
);

// ─── Helpers ─────────────────────────────────────────────────────────────────

const unwrap = (response) => response?.data?.data ?? response?.data;

const getAuthorizedHeaders = (token) => {
  const bearerToken = token || tokenStore.getRawToken();
  return bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {};
};

const getDevicePlatform = () => {
  if (typeof window === 'undefined') return 'Web';
  const ua = window.navigator.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua)) return 'IOS';
  if (/Macintosh/.test(ua) && window.navigator.maxTouchPoints > 1) return 'IOS';
  if (/Android/.test(ua)) return 'Android';
  return 'Web';
};

const appContext = () => ({
  platform: getDevicePlatform(),
  appType: 'Admin',
  applicationId: APPLICATION_ID || undefined,
});

const getExternalProviderLoginUrl = (provider, returnUrl) => {
  const ctx = appContext();
  const encodedReturnUrl = encodeURIComponent(
    returnUrl || (typeof window !== 'undefined' ? window.location.origin : ''),
  );
  const params = [
    `returnUrl=${encodedReturnUrl}`,
    `platform=${encodeURIComponent(ctx.platform)}`,
    `appType=${encodeURIComponent(ctx.appType)}`,
  ];
  if (ctx.applicationId) params.push(`applicationId=${encodeURIComponent(ctx.applicationId)}`);
  return `${IDENTITY_BASE_URL}/api/${IDENTITY_API_VERSION}/auth/providers/login/${encodeURIComponent(provider || 'Microsoft')}?${params.join('&')}`;
};

// ─── Auth service ─────────────────────────────────────────────────────────────

const identityService = {
  getDevicePlatform,
  getExternalProviderLoginUrl,

  // POST /auth/login
  loginWithEmail: async ({ email, password, rememberMe }) => {
    const response = await identityApi.post('/auth/login', {
      email,
      password,
      rememberMe: !!rememberMe,
      ...appContext(),
    });
    return unwrap(response);
  },

  // POST /auth/login/phone
  loginWithPhone: async (phoneNumber) => {
    const response = await identityApi.post('/auth/login/phone', {
      phoneNumber,
      applicationId: appContext().applicationId,
    });
    return unwrap(response);
  },

  // POST /auth/login/verification?userId={id}
  verifyLoginMfa: async (userId, code) => {
    const response = await identityApi.post(`/auth/login/verification?userId=${encodeURIComponent(userId)}`, {
      code,
      ...appContext(),
    });
    return unwrap(response);
  },

  // POST /auth/login/verification/resend?userId={id}
  resendLoginMfa: async (userId) => {
    const response = await identityApi.post(`/auth/login/verification/resend?userId=${encodeURIComponent(userId)}`);
    return unwrap(response);
  },

  // POST /auth/login/phone/verification?userId={id}
  verifyPhoneLoginMfa: async (userId, code) => {
    const response = await identityApi.post(`/auth/login/phone/verification?userId=${encodeURIComponent(userId)}`, {
      code,
      ...appContext(),
    });
    return unwrap(response);
  },

  // POST /auth/token/refresh
  refreshToken: async ({ token, refreshToken }) => {
    const response = await identityApi.post('/auth/token/refresh', { token, refreshToken });
    return unwrap(response);
  },

  // POST /auth/passkey/discover
  discoverPasskeys: async ({ email, phoneNumber }) => {
    const response = await identityApi.post('/auth/passkey/discover', {
      email: email || undefined,
      phoneNumber: phoneNumber || undefined,
      userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : '',
      ...appContext(),
    });
    return unwrap(response);
  },

  // POST /auth/passkey/challenge
  getPasskeyChallenge: async ({ userId, email, phoneNumber }) => {
    const response = await identityApi.post('/auth/passkey/challenge', {
      userId: userId || undefined,
      email: email || undefined,
      phoneNumber: phoneNumber || undefined,
      userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : '',
      ...appContext(),
    });
    return unwrap(response);
  },

  // POST /auth/passkey/authenticate
  authenticateWithPasskey: async (payload) => {
    const response = await identityApi.post('/auth/passkey/authenticate', {
      credentialId: payload.credentialId,
      signature: payload.signature || undefined,
      authenticatorData: payload.authenticatorData || undefined,
      clientDataJSON: payload.clientDataJSON || undefined,
      challenge: payload.challenge || undefined,
      email: payload.email || undefined,
      phoneNumber: payload.phoneNumber || undefined,
      applicationId: payload.applicationId || APPLICATION_ID || undefined,
      ...appContext(),
    });
    return unwrap(response);
  },

  // POST /auth/passkey/register (authenticated)
  registerPasskey: async (payload) => {
    const response = await identityApi.post(
      '/auth/passkey/register',
      {
        deviceName: payload.deviceName,
        passkeyType: payload.passkeyType,
        publicKey: payload.publicKey,
        credentialId: payload.credentialId,
        deviceInfo: payload.deviceInfo || undefined,
        ...appContext(),
      },
      { headers: getAuthorizedHeaders() },
    );
    return unwrap(response);
  },

  // GET /auth/passkey/available
  getAvailablePasskeyTypes: async () => {
    const response = await identityApi.get('/auth/passkey/available');
    return unwrap(response);
  },

  // GET /auth/passkey/list (authenticated)
  getUserPasskeys: async () => {
    const response = await identityApi.get('/auth/passkey/list', {
      headers: getAuthorizedHeaders(),
    });
    return unwrap(response);
  },

  // DELETE /auth/passkey/{passkeyId} (authenticated)
  deletePasskey: async (passkeyId) => {
    const response = await identityApi.delete(`/auth/passkey/${passkeyId}`, {
      headers: getAuthorizedHeaders(),
    });
    return unwrap(response);
  },

  // GET /userprofile (authenticated)
  getUserProfile: async (token) => {
    const response = await identityApi.get('/userprofile', {
      headers: getAuthorizedHeaders(token),
    });
    return unwrap(response);
  },

  // POST /auth/password/reset/request
  requestPasswordReset: async ({ email }) => {
    const response = await identityApi.post('/auth/password/reset/request', { email });
    return unwrap(response);
  },

  // POST /auth/password/reset/verification
  verifyPasswordResetCode: async ({ email, code }) => {
    const response = await identityApi.post('/auth/password/reset/verification', { email, code });
    return unwrap(response);
  },

  // POST /auth/password/reset
  completePasswordReset: async ({ email, token, newPassword, confirmPassword }) => {
    const response = await identityApi.post('/auth/password/reset', {
      email,
      token,
      newPassword,
      confirmPassword,
    });
    return unwrap(response);
  },

  // POST /auth/token/relogin — exchange Microsoft access token for app credentials
  relogin: async (msAccessToken) => {
    const ctx = appContext();
    const response = await identityApi.post(
      `/auth/token/relogin?applicationId=${encodeURIComponent(ctx.applicationId || '')}`,
      {},
      { headers: { Authorization: `Bearer ${msAccessToken}` } },
    );
    return unwrap(response);
  },
};

export { createApiError };
export default identityService;
